/**
 * Locked evolution chips: a creature not yet discovered shows on the HUD ring and strip as a
 * flat ink silhouette on a muted cel disc (drawCreature's `silhouette` pose), so the chain
 * teases what is coming without spoiling colours or faces.
 *
 * Registered as procedural art ("hudLocked/<assetId>") and handed to the DOM as a URL like
 * every other picture. Chips are drawn tiny (24–40 CSS px, so at most ~120 device px on a
 * DPR 3 phone): one 128 px frame at scale 1 is plenty. It is DOM-only, so a 2× canvas would
 * only cost memory and a second bake.
 */
import { defineToonArt, getToonArtUrl } from "../art-registry.js";
import { drawCreature } from "../creatures/index.js";
import { TIER_ART_SIZES, creatureDiscRadius } from "../art-catalog.js";

var LOCKED_FRAME = 128;
/**
 * A light slate fog laid over the drawn chip (inside its own pixels only). The muting lives in
 * the art rather than in CSS opacity: a see-through chip let the strip's tube and arrowhead
 * show through the last chip as a dark band.
 */
export var LOCKED_CHIP_FOG = "rgba(200, 214, 228, 0.1)";
var defined = Object.create(null);

/** @param {string} assetId */
export function lockedChipArtKey(assetId) {
  return "hudLocked/" + assetId;
}

/**
 * Draws one locked chip in logical units (w × h frame).
 * @param {CanvasRenderingContext2D} c
 * @param {string} assetId
 * @param {number} w
 * @param {number} h
 */
export function drawLockedChip(c, assetId, w, h) {
  c.save();
  c.translate(w / 2, h / 2);
  drawCreature(c, assetId, creatureDiscRadius(w, h), { silhouette: true });
  c.restore();
  // source-atop keeps the chip's own alpha, so the fog never spills past the disc rim.
  c.save();
  c.globalCompositeOperation = "source-atop";
  c.fillStyle = LOCKED_CHIP_FOG;
  c.fillRect(0, 0, w, h);
  c.restore();
}

function ensureDefined(assetId) {
  if (defined[assetId]) return;
  defined[assetId] = true;
  defineToonArt(lockedChipArtKey(assetId), {
    w: LOCKED_FRAME,
    h: LOCKED_FRAME,
    scale: 1,
    name: assetId + "-locked",
    draw: function (c, w, h) {
      drawLockedChip(c, assetId, w, h);
    },
  });
}

/**
 * URL of the locked silhouette chip for a merge tier, or "" for an unknown asset.
 * @param {string} assetId
 * @returns {string}
 */
export function lockedChipImageUrl(assetId) {
  var id = String(assetId || "").trim();
  if (!id || !TIER_ART_SIZES[id]) return "";
  ensureDefined(id);
  return getToonArtUrl(lockedChipArtKey(id));
}
